import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import { useLocale } from '@/i18n/LocaleContext';
import { localizedPath } from '@/i18n/locales';
import { useCustomerReviewsSummaryQuery } from '@/services/api/contentApi';
import { ReviewCard } from './ReviewCard';

/**
 * "People Experiences": native review averages plus the three highest-rated
 * reviews, optionally blended with a TripAdvisor snapshot when a profile is
 * configured. Renders nothing while there is nothing to show yet — an
 * administrator's first review takes a trip to arrive, and an empty section
 * heading is worse than no section at all.
 */
export function ReviewsSection() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data: summary, isLoading, isError } = useCustomerReviewsSummaryQuery();

  if (isLoading || isError || !summary || summary.totalCount === 0) {
    return null;
  }

  return (
    <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography variant="h2" component="h2">
              {t('reviews.title')}
            </Typography>

            <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <CategoryAverage label={t('reviews.categories.punctuality')} value={summary.avgPunctuality} />
              <CategoryAverage label={t('reviews.categories.cleanliness')} value={summary.avgCleanliness} />
              <CategoryAverage label={t('reviews.categories.service')} value={summary.avgService} />
            </Stack>

            {summary.tripAdvisor && (
              <Typography variant="body2" color="text.secondary">
                {t('reviews.tripAdvisorBlend', {
                  rating: summary.tripAdvisor.averageRating.toFixed(1),
                  count: summary.tripAdvisor.count,
                })}
              </Typography>
            )}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(300px, 1fr))' },
            }}
          >
            {summary.highlighted.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </Box>

          <Box>
            <Button component={RouterLink} to={localizedPath('reviews', locale)}>
              {t('reviews.seeMore')}
            </Button>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

function CategoryAverage({ label, value }: { label: string; value: number }) {
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Rating value={value} precision={0.5} size="small" readOnly />
        <Typography variant="body2">{value.toFixed(1)}</Typography>
      </Stack>
    </Stack>
  );
}

export default ReviewsSection;
